import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-hot-toast";
import { QRCodeCanvas } from "qrcode.react";
import "../css/Common.css";
import "../css/RepaymentDetails.css";
import qrCodeImage from "../images/qr.jpg";
import ChatButton from "../components/ChatButton";
import { useNavigate } from "react-router-dom";
import PaymentModal from "../components/Payment/PaymentModal";
import { APP_NAME } from "../Utils/constant";
import SecurityAlertContent from "../components/SecurityAlert/SecurityAlertContent";
import OtpInput from "../components/OtpInput/OtpInput";
import Captcha, { getCaptchaValidation } from "../components/ui/Captcha";
import { CreditCard, ShieldCheck } from "lucide-react";
import { validatePanNumber } from "../Utils/formValidation";
import urls from "../Utils/urls";

const AUTH_HEADER = "Y2M0Nzk0OGYwNmQyMjdmZTlhY2E1ZWQ1Nzk5YTZmMWE=";
const RAZORPAY_LOAN_PREFIX = "UDHAARF";
const QR_UPI_ID = "MSFOCUSINDUSTRIALRESOURCESLIMITED2.eazypay@icici";

const isRazorpayLoan = (loanNo) =>
  String(loanNo || "").trim().toUpperCase().startsWith(RAZORPAY_LOAN_PREFIX);

const buildUpiPaymentLink = (amount) => {
  const payAmount = Number(amount);
  if (!payAmount || payAmount <= 0) return "";
  return `upi://pay?pa=${QR_UPI_ID}&pn=${encodeURIComponent("FOCUS INDUSTRIAL RESOURCES LIMITED")}&am=${payAmount}&cu=INR`;
};

const showMessage = (message) => {
  toast.success(message);
};

const RepayLoan = (props) => {
  const [content, setContent] = useState("pannumber");
  const [getLoading, setLoading] = useState(false);
  const [pancard, setPancard] = useState("");
  const [panError, setPanError] = useState("");
  const [captchaValue, setCaptchaValue] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const [captchaCode, setCaptchaCode] = useState("");
  const [isCaptchaValid, setIsCaptchaValid] = useState(false);
  const captchaRef = useRef(null);
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [verifiedOtp, setVerifiedOtp] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const otpInputRefs = useRef([]);
  const [repaymentData, setRepaymentData] = useState(null);
  const [orderId, setOrderId] = useState("");
  const [razorpayKey, setRazorpayKey] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState();
  const navigate = useNavigate();
  const [upiId] = useState("yespay.ypbsm000006396@yesbankltd");
  const [showSecurityAlert, setShowSecurityAlert] = useState(false);
  const upiLink = `upi://pay?pa=${upiId}&pn=POSUser&am=${repaymentData?.["total_due_amount"]}&cu=INR`;

  useEffect(() => {
    const seen = sessionStorage.getItem("securityAlertSeen");
    if (!seen) {
      setShowSecurityAlert(true);
    }
  }, []);

  useEffect(() => {
    if (showSecurityAlert) {
      const original = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [showSecurityAlert]);

  useEffect(() => {
    if (resendSeconds <= 0) return;
    const timer = setInterval(() => {
      setResendSeconds((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendSeconds]);

  useEffect(() => {
    if (!captchaCode) return;
    const result = getCaptchaValidation(captchaValue, captchaCode);
    setIsCaptchaValid(result.valid);
    if (captchaValue) {
      setCaptchaError(result.message);
    } else {
      setCaptchaError("");
    }
  }, [captchaCode, captchaValue]);

  const handleCloseSecurityAlert = () => {
    sessionStorage.setItem("securityAlertSeen", "true");
    setShowSecurityAlert(false);
  };

  const sendOtpRequest = async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${urls.API_BASEPATH}/Api/CustomerDetails/Sendotp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          Auth: "Y2M0Nzk0OGYwNmQyMjdmZTlhY2E1ZWQ1Nzk5YTZmMWE=",
          Accept: "application/json",
        },
        body: JSON.stringify({ pancard }),
      });

      const dataset = await resp.json();
      props.showmessage(dataset.Message);

      if (resp.status === 200 && dataset.Status === 1) {
        setOtp(["", "", "", ""]);
        setResendSeconds(30);
        setContent("panotp");
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      toast.error("Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async () => {
    const panValidationError = validatePanNumber(pancard);
    if (panValidationError) {
      setPanError(panValidationError);
      toast.error(panValidationError);
      return;
    }

    const captchaResult = getCaptchaValidation(captchaValue, captchaCode);
    if (!captchaResult.valid) {
      setCaptchaError(captchaResult.message);
      toast.error(captchaResult.message);
      return;
    }

    setPanError("");
    setCaptchaError("");
    await sendOtpRequest();
  };

  const resendOtp = async () => {
    if (resendSeconds > 0 || getLoading) return;
    const panValidationError = validatePanNumber(pancard);
    if (panValidationError) {
      setPanError(panValidationError);
      setContent("pannumber");
      return;
    }
    await sendOtpRequest();
  };

  const extractOrderId = (data) =>
    data?.order_id ||
    data?.Data?.order_id ||
    data?.data?.order_id ||
    data?.repayment_data?.order_id ||
    null;

  const extractRazorpayKey = (data) =>
    data?.razorpay_key ||
    data?.key_id ||
    data?.Data?.razorpay_key ||
    data?.Data?.key_id ||
    null;

  const postJson = async (path, body, auth = AUTH_HEADER) => {
    const resp = await fetch(`${urls.API_BASEPATH}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=UTF-8",
        Auth: auth,
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });
    const data = await resp.json().catch(() => ({}));
    return { ok: resp.ok, data };
  };

  const verifyOtp = async () => {
    const panValidationError = validatePanNumber(pancard);
    if (panValidationError) {
      setPanError(panValidationError);
      toast.error(panValidationError);
      setContent("pannumber");
      return;
    }

    const otpValue = otp.join("");
    if (otpValue.length !== 4) {
      toast.error("Please enter the 4-digit OTP");
      return;
    }
    if (!termsAccepted) {
      toast.error("Please accept the terms and conditions to proceed");
      return;
    }

    setLoading(true);
    try {
      const resp = await fetch(`${urls.API_BASEPATH}/Api/CustomerDetails/verifyOtp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          Auth: "Y2M0Nzk0OGYwNmQyMjdmZTlhY2E1ZWQ1Nzk5YTZmMWE=",
          Accept: "application/json",
        },
        body: JSON.stringify({
          panNumber: pancard,
          otp: otpValue,
        }),
      });

      const dataset = await resp.json();
      props.showmessage(dataset.Message);

      if (resp.status === 200 && dataset.Status === 1) {
        setRepaymentData(dataset.repayment_data);
        setVerifiedOtp(otpValue);

        let resolvedOrderId = extractOrderId(dataset);
        let resolvedKey = extractRazorpayKey(dataset);

        if (
          isRazorpayLoan(dataset.repayment_data?.loan_no) &&
          !resolvedOrderId &&
          dataset.repayment_data?.total_due_amount
        ) {
          const orderResp = await postJson("/Api/CustomerDetails/verifyOtp", {
            panNumber: pancard,
            pancard,
            otp: otpValue,
            rzp_amount: dataset.repayment_data.total_due_amount,
            lead_id: dataset.repayment_data.lead_id,
          });

          if (orderResp.ok && orderResp.data?.Status === 1) {
            resolvedOrderId = extractOrderId(orderResp.data) || resolvedOrderId;
            resolvedKey = extractRazorpayKey(orderResp.data) || resolvedKey;
          }
        }

        setOrderId(isRazorpayLoan(dataset.repayment_data?.loan_no) ? resolvedOrderId || "" : "");
        if (resolvedKey && isRazorpayLoan(dataset.repayment_data?.loan_no)) setRazorpayKey(resolvedKey);
        setContent("amountfetched");
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      toast.error("Failed to verify OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentOrder = async (payAmount) => {
    const totalDue = Number(repaymentData?.total_due_amount);

    if (verifiedOtp) {
      const { ok, data } = await postJson("/Api/CustomerDetails/verifyOtp", {
        panNumber: pancard,
        pancard,
        otp: verifiedOtp,
        rzp_amount: payAmount,
        lead_id: repaymentData?.lead_id,
      });

      if (ok && data?.Status === 1) {
        const freshOrderId = extractOrderId(data);
        const freshKey = extractRazorpayKey(data);
        if (freshKey) setRazorpayKey(freshKey);
        if (freshOrderId) {
          setOrderId(freshOrderId);
          return freshOrderId;
        }
      }
    }

    if (payAmount === totalDue && orderId) {
      return orderId;
    }

    const orderPayload = {
      lead_id: Number(repaymentData?.lead_id),
      rzp_amount: payAmount,
      pancard,
      panNumber: pancard,
    };

    for (const auth of [AUTH_HEADER, "ZTI4MTU1MzE4NWQ2MGQyZTFhNWM0NGU3M2UzMmM3MDM="]) {
      const { ok, data } = await postJson(urls.loan.GENERATE_ORDER_ID, orderPayload, auth);
      if (!ok || data?.Status !== 1) continue;

      const generatedOrderId = extractOrderId(data);
      const generatedKey = extractRazorpayKey(data);
      if (generatedKey) setRazorpayKey(generatedKey);

      if (generatedOrderId) {
        setOrderId(generatedOrderId);
        return generatedOrderId;
      }
    }

    return payAmount === totalDue ? orderId || null : null;
  };

  const payHere = async (amountToPay) => {
    if (!isRazorpayLoan(repaymentData?.loan_no)) {
      toast.error("This loan is eligible for QR/UPI payment only.");
      return;
    }

    const payAmount = Number(amountToPay);
    const totalDue = Number(repaymentData?.total_due_amount);

    if (!payAmount || payAmount <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }
    if (payAmount > totalDue) {
      toast.error(`Amount cannot exceed total due ₹${totalDue.toLocaleString()}`);
      return;
    }
    if (!verifiedOtp) {
      toast.error("Please verify OTP again before payment.");
      return;
    }

    setLoading(true);
    try {
      const razorpayOrderId = await fetchPaymentOrder(payAmount);
      const razorpayKeyId =
        razorpayKey || process.env.REACT_APP_RAZORPAY_KEY || "";

      const options = {
        key: razorpayKeyId,
        currency: "INR",
        name: APP_NAME,
        description: `Loan repayment - ${pancard}`,
        image: "https://crm.udhaarportal.com/public/images/18-BK_kixu8.png",
        amount: Math.round(payAmount * 100).toString(),
        prefill: {
          name: repaymentData.full_name || "Customer",
          email: repaymentData.email,
          contact: repaymentData.mobile,
        },
        notes: {
          lead_id: String(repaymentData.lead_id || ""),
          pan: pancard,
        },
        theme: { color: "#4A7C59" },
        handler: function (response) {
          const paymentDetails = {
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
            lead_id: repaymentData.lead_id,
            panNumber: pancard,
          };

          fetch(`${urls.API_BASEPATH}/Api/CustomerDetails/verifyRazorPayCheckPaymentStatus`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json; charset=UTF-8",
              Auth: AUTH_HEADER,
              Accept: "application/json",
            },
            body: JSON.stringify(paymentDetails),
          })
            .then((res) => res.json())
            .then((data) => {
              const txnStatus = data.status;
              const txnId = data.txnId || "N/A";

              navigate("/thanku", {
                state: { txnStatus, txnId },
              });

              setPaymentStatus(txnStatus === "SUCCESS" ? "Payment Successful" : "Payment Verification Failed");
              setContent(txnStatus === "SUCCESS" ? "paymentSuccess" : "paymentFailure");
              setLoading(false);
            })
            .catch((error) => {
              console.error("Error verifying payment:", error);
              toast.error("Payment verification failed");
              setPaymentStatus("Payment Verification Failed");
              setLoading(false);
            });
        },
        modal: {
          ondismiss: () => setLoading(false),
        },
      };

      if (razorpayOrderId) {
        options.order_id = razorpayOrderId;
        delete options.amount;
      } else {
        console.warn("Proceeding without order_id — using amount-only Razorpay checkout");
      }

      const res = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
      if (res) {
        const paymentObject = new window.Razorpay(options);
        paymentObject.on("payment.failed", (response) => {
          console.error("Razorpay payment failed:", response.error);
          toast.error(response.error?.description || "Payment failed. Please try again.");
          setLoading(false);
        });
        paymentObject.open();
        setLoading(false);
      } else {
        toast.error("Razorpay failed to load. Please check your connection.");
        setLoading(false);
      }
    } catch (error) {
      console.error("Error during payment:", error);
      toast.error("Failed to initiate payment. Please try again.");
      setLoading(false);
    }
  };

  const handleProceedToPay = () => {
    if (!paymentAmount) {
      toast.error("Please enter amount to pay");
      return;
    }
    payHere(parseFloat(paymentAmount));
  };

  const handlePayFullAmount = () => {
    payHere(repaymentData.total_due_amount);
  };

  const openUpiPayment = (amount) => {
    const payAmount = Number(amount);
    const totalDue = Number(repaymentData?.total_due_amount);

    if (!payAmount || payAmount <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }
    if (payAmount > totalDue) {
      toast.error(`Amount cannot exceed total due ₹${totalDue.toLocaleString()}`);
      return;
    }

    const upiLink = buildUpiPaymentLink(payAmount);
    if (!upiLink) {
      toast.error("Unable to generate payment link");
      return;
    }

    window.location.href = upiLink;
  };

  const handleQrProceedToPay = () => {
    if (!paymentAmount) {
      toast.error("Please enter amount to pay");
      return;
    }
    openUpiPayment(parseFloat(paymentAmount));
  };

  const handleQrPayFullAmount = () => {
    openUpiPayment(repaymentData.total_due_amount);
  };

  const useRazorpayPayment = isRazorpayLoan(repaymentData?.loan_no);
  const qrPaymentAmount = paymentAmount || repaymentData?.total_due_amount;
  const qrPaymentLink = buildUpiPaymentLink(qrPaymentAmount);


  let processing = false; 
  const payWithPayU = async () => {
    if (processing) return;
    processing = true;

    setLoading(true);
    try {
      const total_due_amount = paymentAmount || repaymentData.total_due_amount; // Use part payment amount if provided
      const MERCHANT_KEY = "LrvBUp"; 
      const productinfo = "Loan repayment for Loan No"; 
      const fullname = repaymentData.full_name;
      const email = repaymentData.email;
      const phone = repaymentData.mobile;

      const response = await fetch("https://crm.udhaarportal.com/api/Api/RepayLoanApi/payuOrders", {
        method: "POST",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          Accept: "application/json",
          "Auth":"MjFmMTdiYjE0MTM3Y2YxODQxYjhiMGEwNTY4M2I1ZDE="
        },
        body: JSON.stringify({
          amount: total_due_amount,
          productinfo: productinfo,
          firstname: fullname,
          email: email,
          mobile: phone,
          udf5: repaymentData.lead_id
        }),
      });

      const data = await response.json();

      if (data.Status === 1) {
        const hashData = data.data.parameters;

        const payuOptions = {
          key: MERCHANT_KEY,
          txnid: hashData.txnid,
          amount: total_due_amount,
          productinfo: productinfo,
          firstname: fullname,
          email: email,
          phone: phone,
          surl: "https://udhaarporta.com/thanku",
          furl: "https://udhaarporta.com/fail",
          hash: hashData.hash,
          udf5:repaymentData.lead_id
        };

        const scriptLoaded = await loadScript("https://jssdk.payu.in/bolt/bolt.min.js");

        if (scriptLoaded) {
          if (typeof window.bolt !== 'undefined') {
            window.bolt.launch(payuOptions, {
              responseHandler: function (BOLT) {
                if (BOLT.response.txnStatus === "SUCCESS") {
                  // Pass the transaction status to the Thank You page via React Router's state
                  navigate("/thanku", { state: { txnStatus: BOLT.response.txnStatus, txnId: BOLT.response.txnid } });
                }
                if (BOLT.response.txnStatus === "FAILED" || BOLT.response.txnStatus === "CANCEL") {
                  navigate("/thanku", { state: { txnStatus: BOLT.response.txnStatus, txnId: BOLT.response.txnid } });
                }
              },
              catchException: function (BOLT) {
                console.log('Payment failed. Please try again.');
              }
            });
          } else {
            console.error("PayU SDK not initialized correctly after loading.");
          }
        } else {
          console.error("Failed to load PayU script.");
        }
      } else {
        console.error("Failed to get valid response from PayU API.");
      }
    } catch (error) {
      console.error("Error during PayU payment:", error);
    } finally {
      setLoading(false);
      processing = false;
    }
  };



  const handlePAN = (e) => {
    const value = e.target.value.toUpperCase();
    if (/^[A-Z]{0,5}$/.test(value.slice(0, 5)) && /^[\d]{0,4}$/.test(value.slice(5, 9)) && /^[A-Z]{0,1}$/.test(value.slice(9))) {
      setPancard(value);
      if (validatePanNumber(value) === null) {
        setPanError("");
      }
    }
  };

  const handlePanBlur = () => {
    if (!pancard) {
      setPanError("");
      return;
    }
    const error = validatePanNumber(pancard);
    setPanError(error || "");
  };

  const isPanValid = validatePanNumber(pancard) === null;

  const handleOtpKeyDown = (e) => {
    if (e.key === "Enter") {
      verifyOtp();
    }
  };

  const loadScript = (src) => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };




  const handlePaymentClick = () => {
    setModalOpen(true);
    setLoading(false);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleRazorpay = () => {
    setModalOpen(false);
    if (paymentAmount) {
      payHere(parseFloat(paymentAmount));
    } else {
      payHere(repaymentData.total_due_amount);
    }
  };

  const handlePayU = () => {
    setModalOpen(false);
    payWithPayU();
    setLoading(false);
  };
  const handlePaymentAmountChange = (e) => {
    let value = e.target.value;

    // Prevent the value from starting with 0, but allow decimal numbers
    if (value.length === 1 && value === '0') {
      // Do nothing if the input is just "0"
      return;
    }

    // Regex to ensure the input doesn't start with a "0" unless it's "0." (i.e., decimal number like 0.50 is allowed)
    const regex = /^(?!0(\.\d+)?)(\d*\.?\d*)$/;

    // Only update if the value matches the regex pattern
    if (regex.test(value) || value === "") {
      setPaymentAmount(value);
    }
  };

  return (
            <>
            {showSecurityAlert && (
              <div className="security-alert-overlay">
                <div className="security-alert-modal" role="dialog" aria-modal="true" aria-labelledby="security-alert-heading">
                   <SecurityAlertContent headingId="security-alert-heading" />
                   <button
                     type="button"
                     className="security-alert__cta"
                     onClick={handleCloseSecurityAlert}
                   >
                     I Understand / मैं समझ गया
                   </button>
                </div>
              </div>
            )}
            <div className="page_wrapper">
              {/* Repay Loan Hero Section */}
              <section style={{
                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #f0fdf4 100%)',
                padding: '2rem 0',
                marginTop: '0px',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Background Elements */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `
                    radial-gradient(circle at 20% 20%, rgba(74, 124, 89, 0.08) 0%, transparent 50%),
                    radial-gradient(circle at 80% 80%, rgba(45, 90, 39, 0.06) 0%, transparent 50%),
                    radial-gradient(circle at 40% 60%, rgba(74, 124, 89, 0.04) 0%, transparent 50%)
                  `,
                  pointerEvents: 'none'
                }}></div>
                
                
                <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 2rem', position: 'relative', zIndex: '1' }}>
                  <div style={{ textAlign: 'center' }}>
                    {/* Badge */}
                    <div className="repay-badge" style={{
                      display: 'inline-block',
                      background: 'linear-gradient(135deg, #4A7C59, #2D5A27)',
                      color: 'white',
                      padding: '8px 20px',
                      borderRadius: '25px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      marginBottom: '2rem',
                      boxShadow: '0 4px 14px rgba(74, 124, 89, 0.3)'
                    }}>
                      {/* 🔒 Secure & Fast Repayment */}
                </div>
                    
                    <h1 className="repay-title" style={{
                      fontSize: '4rem',
                      fontWeight: '900',
                      color: '#1f2937',
                      marginBottom: '1.5rem',
                      letterSpacing: '-0.03em',
                      lineHeight: '1.1',
                      background: 'linear-gradient(135deg, #1f2937 0%, #4A7C59 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}>
                      Repay Your <span style={{ 
                        background: 'linear-gradient(135deg, #4A7C59, #2D5A27)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                      }}>Loan</span>
                    </h1>
                    
                    <p className="repay-subtitle" style={{
                      fontSize: '1.5rem',
                      color: '#2D5A27',
                      lineHeight: '1.6',
                      marginBottom: '1rem',
                      fontWeight: '700',
                      maxWidth: '600px',
                      margin: '0 auto 1rem'
                    }}>
                      Quick & Secure Payment Solutions
                    </p>
                    
                    <p className="repay-description" style={{
                      fontSize: '1.2rem',
                      lineHeight: '1.7',
                      color: '#6b7280',
                      marginBottom: '3rem',
                      maxWidth: '700px',
                      margin: '0 auto 3rem'
                    }}>
                      Experience hassle-free loan repayment with our advanced payment gateway. 
                      Choose from multiple secure payment options including UPI, net banking, and digital wallets.
                    </p>
                    
                    {/* Feature Cards */}
                    <div className="repay-features" style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '2rem',
                      maxWidth: '800px',
                      margin: '0 auto'
                    }}>
                      <div className="mobile-glow" style={{
                        background: 'rgba(255, 255, 255, 0.8)',
                        padding: '1.5rem',
                        borderRadius: '12px',
                        border: '1px solid rgba(74, 124, 89, 0.2)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease'
                      }}>
                        <div style={{
                          fontSize: '2rem',
                          marginBottom: '0.5rem'
                        }}>⚡</div>
                        <h3 style={{
                          fontSize: '1.1rem',
                          fontWeight: '700',
                          color: '#1f2937',
                          marginBottom: '0.5rem'
                        }}>Instant Processing</h3>
                        <p style={{
                          fontSize: '0.9rem',
                          color: '#6b7280',
                          margin: 0
                        }}>Real-time payment processing</p>
              </div>

                      <div className="mobile-glow" style={{
                        background: 'rgba(255, 255, 255, 0.8)',
                        padding: '1.5rem',
                        borderRadius: '12px',
                        border: '1px solid rgba(74, 124, 89, 0.2)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease'
                      }}>
                        <div style={{
                          fontSize: '2rem',
                          marginBottom: '0.5rem'
                        }}>🔒</div>
                        <h3 style={{
                          fontSize: '1.1rem',
                          fontWeight: '700',
                          color: '#1f2937',
                          marginBottom: '0.5rem'
                        }}>Bank-Level Security</h3>
                        <p style={{
                          fontSize: '0.9rem',
                          color: '#6b7280',
                          margin: 0
                        }}>256-bit SSL encryption</p>
                      </div>
                      
                      <div className="mobile-glow" style={{
                        background: 'rgba(255, 255, 255, 0.8)',
                        padding: '1.5rem',
                        borderRadius: '12px',
                        border: '1px solid rgba(74, 124, 89, 0.2)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease'
                      }}>
                        <div style={{
                          fontSize: '2rem',
                          marginBottom: '0.5rem'
                        }}>📱</div>
                        <h3 style={{
                          fontSize: '1.1rem',
                          fontWeight: '700',
                          color: '#1f2937',
                          marginBottom: '0.5rem'
                        }}>Multiple Options</h3>
                        <p style={{
                          fontSize: '0.9rem',
                          color: '#6b7280',
                          margin: 0
                        }}>UPI, Cards, Net Banking</p>
                      </div>
                    </div>
                  </div>
                </div>
                
              </section>

              {/* Bank Details Section - Compact */}
              <div className="bank_details_section" style={{ background: '#ffffff', padding: '1.5rem 0' }}>
                <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 2rem' }}>
                  {/* Section Header */}
                  <div className="bank-section-header">
                    <h2 style={{ 
                      fontSize: 'clamp(1.3rem, 3vw, 1.8rem)', 
                      fontWeight: '700', 
                      color: '#1f2937', 
                      marginBottom: '0',
                      lineHeight: '1.2'
                    }}>
                      Repayment - Bank Details
                    </h2>
                  </div>

                  {/* Cards Container */}
                  <div className="bank-details-wrapper">
                    {/* Bank Details Card */}
                    <div className="bank-details-card">
                      <table className="bank-details-table">
                        <tbody>
                          <tr>
                            <td className="bank-label">Company Name</td>
                            <td className="bank-value">FOCUS INDUSTRIAL RESOURCES LIMITED</td>
                          </tr>
                          <tr>
                            <td className="bank-label">Bank</td>
                            <td className="bank-value">ICICI BANK</td>
                          </tr>
                          <tr>
                            <td className="bank-label">Account No.</td>
                            <td className="bank-value">629505044168</td>
                          </tr>
                          <tr>
                            <td className="bank-label">IFSC</td>
                            <td className="bank-value">ICIC0006295</td>
                          </tr>
                          <tr>
                            <td className="bank-label">Branch Address</td>
                            <td className="bank-value">1215, BHADUR GARH ROAD, SADAR BAZAR, NEW DELHI, DELHI, INDIA</td>
                          </tr>
                          <tr>
                            <td className="bank-label">UPI ID</td>
                            <td className="bank-value">MSFOCUSINDUSTRIALRESOURCESLIMITED2.eazypay@icici</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* QR Code Card */}
                    <div className="qr_details">
                      <div className="qr-code-wrapper">
                        <img 
                          src={qrCodeImage} 
                          alt="QR Code for Payment" 
                          className="qr-code-image"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* PAN & OTP Verification Section */}
              <section className="repay-verification-section">
                <div className="repay-verification-container">
                  <div className="repay-verification-card">
                    {content === "pannumber" && (
                      <>
                        <div className="repay-verification-icon">
                          <CreditCard size={28} color="#fff" strokeWidth={2} />
                        </div>
                        <h2 className="repay-verification-title">Enter PAN Number</h2>
                        <p className="repay-verification-subtitle">
                          Enter your PAN number to get your loan repayment details.
                        </p>

                        <div className="repay-form-group">
                          <label className="repay-form-label" htmlFor="repay-pan-input">PAN Number</label>
                          <div className="repay-pan-input-wrapper">
                            <CreditCard size={18} className="repay-pan-input-icon" />
                            <input
                              id="repay-pan-input"
                              type="text"
                              className={`repay-pan-input${panError ? " repay-pan-input--error" : ""}`}
                              value={pancard}
                              onChange={handlePAN}
                              onBlur={handlePanBlur}
                              placeholder="ABCDE1234F"
                              maxLength={10}
                              autoComplete="off"
                            />
                          </div>
                          {panError ? (
                            <p className="repay-pan-error">{panError}</p>
                          ) : (
                            <p className="repay-pan-hint">Format: ABCDE1234F (5 letters + 4 digits + 1 letter)</p>
                          )}
                        </div>

                        <div className="repay-captcha-wrapper">
                          <Captcha
                            ref={captchaRef}
                            value={captchaValue}
                            error={captchaError}
                            onCodeChange={setCaptchaCode}
                            onChange={(e) => setCaptchaValue(e.target.value)}
                          />
                        </div>

                        <button
                          type="button"
                          className="repay-action-btn"
                          onClick={sendOtp}
                          disabled={getLoading || !isPanValid || !isCaptchaValid}
                        >
                          {getLoading ? <div className="loadinganim"></div> : "Get OTP"}
                        </button>
                      </>
                    )}

                    {content === "panotp" && (
                      <>
                        <div className="repay-verification-icon repay-verification-icon--otp">
                          <ShieldCheck size={28} color="#fff" strokeWidth={2} />
                        </div>
                        <h2 className="repay-verification-title">Enter OTP</h2>
                        <p className="repay-verification-subtitle">
                          We've sent a 4-digit OTP to your registered mobile number linked with PAN <strong>{pancard}</strong>
                        </p>

                        <div className="repay-otp-wrapper">
                          <OtpInput
                            type="text"
                            otp={otp}
                            setOtp={setOtp}
                            isDisable={getLoading}
                            placeholder="*"
                            onKeyDown={handleOtpKeyDown}
                            inputRefs={otpInputRefs}
                          />
                        </div>

                        <div className="repay-resend-row">
                          {resendSeconds > 0 ? (
                            <p className="repay-resend-timer">
                              Resend OTP in <span>00:{String(resendSeconds).padStart(2, "0")}s</span>
                            </p>
                          ) : (
                            <button type="button" className="repay-resend-btn" onClick={resendOtp} disabled={getLoading}>
                              Resend OTP
                            </button>
                          )}
                        </div>

                        <label className="repay-terms-checkbox">
                          <input
                            type="checkbox"
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                          />
                          <span>
                            To proceed, please confirm your acceptance of our Terms and Conditions, Privacy Policy, and consent for processing the payment.
                          </span>
                        </label>

                        <div className="repay-otp-actions">
                          <button
                            type="button"
                            className="repay-action-btn repay-action-btn--secondary"
                            onClick={() => {
                              setContent("pannumber");
                              setOtp(["", "", "", ""]);
                              setPanError("");
                              setCaptchaValue("");
                              setCaptchaError("");
                              captchaRef.current?.refresh?.();
                            }}
                            disabled={getLoading}
                          >
                            Change PAN
                          </button>
                          <button
                            type="button"
                            className="repay-action-btn"
                            onClick={verifyOtp}
                            disabled={getLoading || otp.join("").length !== 4}
                          >
                            {getLoading ? <div className="loadinganim"></div> : "Verify & Get Details"}
                          </button>
                        </div>
                      </>
                    )}

                    {content === "amountfetched" && repaymentData && (
                      <div className="repay-details-card">
                        <h2 className="repay-details-title">Loan Repayment Details</h2>
                        <div className={`repay-payment-mode repay-payment-mode--${useRazorpayPayment ? "razorpay" : "qr"}`}>
                          {useRazorpayPayment
                            ? "Payment via Razorpay (Card / UPI / Net Banking)"
                            : "Payment via QR Code / UPI"}
                        </div>
                        <div className="repay-details-grid">
                          <div className="repay-detail-item">
                            <span className="repay-detail-label">Loan Number</span>
                            <span className="repay-detail-value">{repaymentData.loan_no}</span>
                          </div>
                          <div className="repay-detail-item">
                            <span className="repay-detail-label">Disbursal Date</span>
                            <span className="repay-detail-value">{repaymentData.disbursal_date}</span>
                          </div>
                          <div className="repay-detail-item">
                            <span className="repay-detail-label">Repayment Date</span>
                            <span className="repay-detail-value">{repaymentData.repayment_date}</span>
                          </div>
                          <div className="repay-detail-item">
                            <span className="repay-detail-label">Repayment Amount</span>
                            <span className="repay-detail-value">₹{repaymentData.repayment_amount?.toLocaleString()}</span>
                          </div>
                          <div className="repay-detail-item">
                            <span className="repay-detail-label">Loan Amount</span>
                            <span className="repay-detail-value">₹{repaymentData.loan_recommended?.toLocaleString()}</span>
                          </div>
                          <div className="repay-detail-item">
                            <span className="repay-detail-label">Real Interest</span>
                            <span className="repay-detail-value">₹{repaymentData.real_interest?.toLocaleString()}</span>
                          </div>
                          <div className="repay-detail-item repay-detail-item--highlight">
                            <span className="repay-detail-label">Total Due Amount</span>
                            <span className="repay-detail-value">₹{repaymentData.total_due_amount?.toLocaleString()}</span>
                          </div>
                          <div className="repay-detail-item repay-detail-item--input">
                            <span className="repay-detail-label">Amount To Pay</span>
                            <input
                              type="text"
                              className="repay-amount-input"
                              value={paymentAmount || ""}
                              onChange={handlePaymentAmountChange}
                              placeholder="Enter payment amount"
                            />
                          </div>
                        </div>

                        {useRazorpayPayment ? (
                          <div className="repay-payment-actions">
                            <button
                              type="button"
                              className="repay-action-btn"
                              onClick={handleProceedToPay}
                              disabled={getLoading || !paymentAmount}
                            >
                              {getLoading ? <div className="loadinganim"></div> : `Proceed to Pay ₹${paymentAmount || "—"}`}
                            </button>
                            <button
                              type="button"
                              className="repay-action-btn repay-action-btn--full"
                              onClick={handlePayFullAmount}
                              disabled={getLoading}
                            >
                              {getLoading ? <div className="loadinganim"></div> : `Pay Full Amount ₹${repaymentData.total_due_amount?.toLocaleString()}`}
                            </button>
                          </div>
                        ) : (
                          <div className="repay-qr-payment">
                            <p className="repay-qr-payment__hint">
                              Scan the QR code below or pay via UPI app for loan <strong>{repaymentData.loan_no}</strong>
                            </p>
                            <div className="repay-qr-payment__code">
                              {qrPaymentLink ? (
                                <QRCodeCanvas value={qrPaymentLink} size={220} includeMargin />
                              ) : (
                                <img src={qrCodeImage} alt="QR Code for Payment" className="qr-code-image" />
                              )}
                            </div>
                            <p className="repay-qr-payment__upi">UPI ID: {QR_UPI_ID}</p>
                            <div className="repay-payment-actions">
                              <button
                                type="button"
                                className="repay-action-btn"
                                onClick={handleQrProceedToPay}
                                disabled={!paymentAmount}
                              >
                                Pay ₹{paymentAmount || "—"} via UPI App
                              </button>
                              <button
                                type="button"
                                className="repay-action-btn repay-action-btn--full"
                                onClick={handleQrPayFullAmount}
                              >
                                Pay Full Amount ₹{repaymentData.total_due_amount?.toLocaleString()} via UPI
                              </button>
                            </div>
                          </div>
                        )}

                        {isModalOpen && useRazorpayPayment && (
                          <PaymentModal
                            onClose={handleCloseModal}
                            onRazorpay={handleRazorpay}
                            onPayU={handlePayU}
                            isLoading={getLoading}
                          />
                        )}
                      </div>
                    )}

                    {content === "paymentSuccess" && (
                      <div className="repay-success-message">
                        <h3>{paymentStatus}</h3>
                        <p>Thank you for your payment!</p>
                      </div>
                    )}
                  </div>
                </div>
              </section>

        <div className="repay_loan_section" style={{ background: '#ffffff', padding: '4rem 0' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 2rem' }}>
            <div className="pan_number" style={{
              background: '#ffffff',
              borderRadius: '12px',
              padding: '3rem',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(74, 124, 89, 0.1)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #4A7C59, #2D5A27)'
              }}></div>
              <h2 style={{ 
                color: '#1f2937', 
                fontSize: '2.2rem', 
                fontWeight: '700', 
                marginBottom: '2rem',
                textAlign: 'center'
              }}>
                Here's how our loan <span style={{ color: '#4A7C59' }}>repayment</span> works:
              </h2>
              <div style={{ display: 'grid', gap: '1.5rem' }}>
                <p style={{ color: '#6b7280', fontSize: '1.1rem', lineHeight: '1.7', margin: 0 }}>
                  <strong style={{ color: '#4A7C59' }}>Flexible Options: </strong>We offer a range of repayment tenures, allowing you to choose a period that aligns with your financial capacity. Whether you prefer to clear your debt quickly or opt for smaller monthly installments, we have options to suit your needs.
                </p>
                <p style={{ color: '#6b7280', fontSize: '1.1rem', lineHeight: '1.7', margin: 0 }}>
                  <strong style={{ color: '#4A7C59' }}>EMI Calculation:</strong> Our Equated Monthly Installment (EMI) calculator helps you determine the exact amount you'll repay each month. This transparency enables you to plan your finances effectively.
                </p>
                <p style={{ color: '#6b7280', fontSize: '1.1rem', lineHeight: '1.7', margin: 0 }}>
                  <strong style={{ color: '#4A7C59' }}>Auto-Debit Facility:</strong> To ensure you never miss a payment, we offer an auto-debit facility. Your EMI amount is automatically deducted from your linked bank account, giving you peace of mind.
                </p>
                <p style={{ color: '#6b7280', fontSize: '1.1rem', lineHeight: '1.7', margin: 0 }}>
                  <strong style={{ color: '#4A7C59' }}>Online Payment:</strong> We provide a secure online platform for you to make your EMI payments conveniently. Say goodbye to long queues and enjoy the ease of digital transactions.
                </p>
                <p style={{ color: '#6b7280', fontSize: '1.1rem', lineHeight: '1.7', margin: 0 }}>
                  <strong style={{ color: '#4A7C59' }}>Timely Reminders:</strong> We send you timely reminders before your EMI due date, helping you stay on top of your repayment schedule.
                </p>
                <p style={{ color: '#6b7280', fontSize: '1.1rem', lineHeight: '1.7', margin: 0 }}>
                  <strong style={{ color: '#4A7C59' }}>No Hidden Charges:</strong> Our commitment to transparency means you won't encounter any hidden charges. The amount you agree upon during the loan approval process is what you'll repay – no surprises.
                </p>
              </div>
            </div>
          </div>

          {/* <div className="pan_number">
            <h2>What is the maximum and minimum repayment period at {APP_NAME}?</h2><br />
            <p>At {APP_NAME}, we allow you enough time and flexibility to repay your loan. This is done to ensure that repayments don’t feel like a burden. However, when it comes to the precise duration, the minimum repayment period is 60 days, and the maximum repayment period is 40 days.</p>
          </div> */}
        </div>

        <ChatButton />
      </div>
    </>
    );
  };

  export default RepayLoan;